import { ContentLinkCreateInput } from '@vivport/backend/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContentLink,
  deleteContentLink,
  listContentLinks,
} from '../api/content-link-api';
import { contentKeys } from './useContentQueries';

export const contentLinkKeys = {
  all: ['content-links'] as const,
  byContent: (contentId: number) => [...contentLinkKeys.all, contentId] as const,
};

export const useContentLinks = (contentId: number) =>
  useQuery({
    queryKey: contentLinkKeys.byContent(contentId),
    queryFn: () => listContentLinks(contentId),
    enabled: contentId > 0,
  });

export const useCreateContentLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, payload }: { contentId: number; payload: ContentLinkCreateInput }) =>
      createContentLink(contentId, payload),
    onSuccess: (_, { contentId, payload }) => {
      queryClient.invalidateQueries({ queryKey: contentLinkKeys.byContent(contentId) });
      queryClient.invalidateQueries({ queryKey: contentLinkKeys.byContent(payload.targetContentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(payload.targetContentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
    },
  });
};

export const useDeleteContentLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, targetContentId }: { contentId: number; targetContentId: number }) =>
      deleteContentLink(contentId, targetContentId),
    onSuccess: (_, { contentId, targetContentId }) => {
      queryClient.invalidateQueries({ queryKey: contentLinkKeys.byContent(contentId) });
      queryClient.invalidateQueries({ queryKey: contentLinkKeys.byContent(targetContentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(targetContentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
    },
  });
};
